// DragOverlayCard — компактный «призрак» перетаскиваемого элемента под курсором/
// пальцем. Рендерится внутри <DragOverlay> (DnDProvider), не привязан к layout
// списка — поэтому одинаков для grid и list источников.
//
// Дизайн: плотная капсула (Linear/Notion) — иконка типа + заголовок. Только
// shadcn-токены, Tabler-иконки. Лёгкий tilt + тень — тактильность «Картотеки».
// При reduced-motion декоративный наклон убираем (ровная капсула).

import type { SetItemType } from "@studyhub/shared/contracts/materials";
import {
	IconCards,
	IconChecklist,
	IconFileText,
	IconFolder,
	IconHeadphones,
	IconNotebook,
	IconPhoto,
	type IconProps,
	IconStack2,
} from "@tabler/icons-react";
import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { cn } from "@/lib/cn";
import type { DragItem } from "@/stores/drag-session";

const TYPE_ICON: Record<SetItemType, ComponentType<IconProps>> = {
	material: IconStack2,
	// Папку перетаскивать в коллекцию нельзя (checkSetItemEligible это запрещает),
	// но тип обязан быть покрыт — Record не допускает пропусков.
	folder: IconFolder,
	note: IconNotebook,
	test: IconChecklist,
	flashcard_set: IconCards,
	audio_recap: IconHeadphones,
	visual_analysis: IconPhoto,
	essay: IconFileText,
};

interface DragOverlayCardProps {
	item: DragItem;
}

export function DragOverlayCard({ item }: DragOverlayCardProps): React.ReactElement {
	const { t } = useTranslation("library");
	const reducedMotion = usePrefersReducedMotion();
	const Icon = TYPE_ICON[item.type] ?? IconStack2;

	return (
		<div
			className={cn(
				"pointer-events-none flex max-w-[18rem] items-center gap-2.5 rounded-[var(--radius-card)] border border-border bg-card px-3 py-2.5 shadow-e3",
				// Декоративный наклон — только при обычной анимации (a11y).
				!reducedMotion && "[transform:rotate(-2deg)]",
			)}
			aria-hidden
		>
			<span className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-primary/10 text-primary">
				<Icon size={18} stroke={1.75} />
			</span>
			<span className="min-w-0 flex-1">
				<span className="block truncate text-sm font-semibold leading-tight text-foreground">
					{item.title}
				</span>
				<span className="block text-2xs font-medium uppercase tracking-wide text-muted-foreground">
					{t(`artifact.type.${item.type}`)}
				</span>
			</span>
		</div>
	);
}
